import { useState, useEffect } from "react";
import { Sparkles, X, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const DISMISS_KEY = "coachiq_founder_banner_dismissed";

/**
 * Compact status badge for Founding Coaches.
 *
 * • Rendered inside AppShell on every page.
 * • Dismissible — the state is persisted in localStorage per browser.
 * • Deliberately understated: no upsell copy, no CTA to buy again.
 */
export default function FounderBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(DISMISS_KEY) === "1");
    } catch { /* ignore */ }
  }, []);

  if (!user?.is_founder || dismissed) return null;

  const dismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className="mb-4 inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200/70 pl-2 pr-1 py-1 hover:bg-amber-100/60 transition-colors"
      data-testid="founder-banner"
    >
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-white shrink-0">
        <Sparkles className="w-3 h-3" strokeWidth={2.8} />
      </span>
      <span className="text-[11px] font-bold uppercase tracking-widest text-amber-900">Founder</span>
      <Link
        to="/settings/billing"
        className="text-[11px] font-semibold text-amber-900/80 hover:text-amber-900 inline-flex items-center gap-0.5"
        data-testid="founder-banner-billing"
      >
        Billing <ChevronRight className="w-3 h-3" />
      </Link>
      <button
        type="button"
        onClick={dismiss}
        data-testid="founder-banner-dismiss"
        className="ml-1 w-5 h-5 rounded-full flex items-center justify-center text-amber-800/60 hover:text-amber-900 hover:bg-amber-200/50"
        aria-label="Dismiss founder banner"
        title="Hide"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}
